import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import { useCreateReport } from "../../hooks/useCreateReport";
import { preventDialogDismissForDropdown } from "../../lib/prevent-dialog-dismiss-for-dropdown";
import { PreFlightHubInputCard } from "../PreFlightHub/PreFlightHubInputCard";
import type { PreflightMarketWithCount } from "../../types/preflight.types";

export interface NewReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tenant's markets; the form requires one of them (R12). */
  markets: PreflightMarketWithCount[];
  /** From tenant_settings.defaultBrand — sent as the report brandName. */
  defaultBrand: string | null;
  isDefaultBrandLoading?: boolean;
}

/**
 * Report creation as a modal (R11). It owns the create mutation so a successful
 * create closes the dialog; the mutation clears the draft, which bumps the session
 * epoch that tears down the upload queue. Dismissing without submitting leaves the
 * draft alone, so an accidental close costs the user nothing.
 */
export function NewReportDialog({
  open,
  onOpenChange,
  markets,
  defaultBrand,
  isDefaultBrandLoading = false,
}: NewReportDialogProps) {
  const { t } = useTranslation();
  const createReport = useCreateReport();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="form"
        onPointerDownOutside={preventDialogDismissForDropdown}
        onInteractOutside={preventDialogDismissForDropdown}
      >
        {/* Full-bleed rule under the header, matching the design-system form footer. */}
        <DialogHeader className="border-border -mx-5 border-b px-5 pb-4">
          <DialogTitle>
            {t("preflight.newReport.title", "New Pre Flight report")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "preflight.newReport.description",
              "Upload creatives or paste URLs to score against platform standards.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto">
          <PreFlightHubInputCard
            markets={markets}
            defaultBrand={defaultBrand}
            isDefaultBrandLoading={isDefaultBrandLoading}
            isSubmitting={createReport.isPending}
            onCancel={() => onOpenChange(false)}
            onAnalyze={(payload) =>
              createReport.mutate(payload, {
                onSuccess: () => onOpenChange(false),
              })
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
